// LifeBehaviorMath.ts: Mathematical models for structuring organic life behaviors
// Implements growth equations, metabolic dynamics, oscillations, and homeostatic regulation

import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';

/**
 * MATHEMATICAL MODEL 1: Logistic Growth
 * dN/dt = rN(1 - N/K)
 * Where:
 * - N = population/size
 * - r = growth rate
 * - K = carrying capacity
 */
export interface GrowthParameters {
    initialSize: number;
    growthRate: number;
    carryingCapacity: number;
    currentSize: number;
    time: number;
}

export class GrowthModel {
    public calculateLogisticGrowth(params: GrowthParameters, dt: number): number {
        // dN/dt = rN(1 - N/K)
        const dN = params.growthRate * params.currentSize * 
                   (1 - params.currentSize / params.carryingCapacity) * dt;
        return Math.max(0, params.currentSize + dN);
    }
    
    public calculateExponentialGrowth(params: GrowthParameters, dt: number): number {
        // N(t) = N₀ * e^(rt)
        return params.currentSize * Math.exp(params.growthRate * dt);
    }
    
    public calculateGompertzGrowth(params: GrowthParameters, dt: number): number {
        // dN/dt = rN * ln(K/N)
        const dN = params.growthRate * params.currentSize * 
                   Math.log(params.carryingCapacity / params.currentSize) * dt;
        return Math.max(0, Math.min(params.carryingCapacity, params.currentSize + dN));
    }
}

/**
 * MATHEMATICAL MODEL 2: Metabolic Energy Dynamics
 * dE/dt = α·P - β·E - γ·E²
 * Where:
 * - E = energy level
 * - P = production rate
 * - α, β, γ = metabolic parameters
 */
export interface MetabolicParameters {
    currentEnergy: number;
    productionRate: number;
    consumptionRate: number;
    efficiencyFactor: number;
    maxEnergy: number;
}

export class MetabolicModel {
    public calculateEnergyDynamics(params: MetabolicParameters, dt: number): number {
        // dE/dt = α·P - β·E - γ·E²
        const energyProduction = params.productionRate * params.efficiencyFactor * dt;
        const energyConsumption = params.consumptionRate * params.currentEnergy * dt;
        const energyWaste = 0.001 * params.currentEnergy * params.currentEnergy * dt;
        
        const dE = energyProduction - energyConsumption - energyWaste;
        return Math.max(0, Math.min(params.maxEnergy, params.currentEnergy + dE));
    }
    
    public calculateATPProduction(glucoseLevel: number, efficiency: number): number {
        // Simplified ATP production from glucose
        // Real cells produce ~36 ATP per glucose, simplified here
        return glucoseLevel * efficiency * 36;
    }
}

/**
 * MATHEMATICAL MODEL 3: Van der Pol Oscillator (Heartbeat, Breathing)
 * d²x/dt² - μ(1 - x²)dx/dt + x = 0
 * Where μ is the damping parameter
 */
export interface OscillatorState {
    position: number;
    velocity: number;
    damping: number;
    frequency: number;
    target: number;
}

export class OscillatorModel {
    public calculateVanDerPolOscillator(state: OscillatorState, dt: number): OscillatorState {
        // Van der Pol oscillator: d²x/dt² - μ(1 - x²)dx/dt + ω²x = 0
        const mu = state.damping;
        const omega = state.frequency;
        
        // Convert to system of first-order ODEs
        const x = state.position - state.target;
        const v = state.velocity;
        
        // dx/dt = v
        const dx = v;
        
        // d²x/dt² = dv/dt = μ(1 - x²)v - ω²x
        const dv = mu * (1 - x * x) * v - omega * omega * x;
        
        return {
            position: state.position + dx * dt,
            velocity: state.velocity + dv * dt,
            damping: state.damping,
            frequency: state.frequency,
            target: state.target
        };
    }
    
    public calculateLimitCycle(state: OscillatorState, dt: number): OscillatorState {
        // Sinusoidal limit cycle: x = r·cos(ωt), y = r·sin(ωt)
        const time = state.position; // Use position as phase
        const amplitude = 1.0;
        const frequency = state.frequency;
        
        const newPosition = amplitude * Math.cos(frequency * time);
        const newVelocity = -amplitude * frequency * Math.sin(frequency * time);
        
        return {
            position: newPosition,
            velocity: newVelocity,
            damping: state.damping,
            frequency: state.frequency,
            target: state.target
        };
    }
}

/**
 * MATHEMATICAL MODEL 4: Homeostatic Regulation
 * dx/dt = β(S - T) where S is setpoint, T is current value
 */
export interface HomeostaticParameters {
    currentValue: number;
    setPoint: number;
    regulationStrength: number;
    disturbance: number;
}

export class HomeostaticModel {
    public calculateRegulation(params: HomeostaticParameters, dt: number): number {
        // dx/dt = β(S - T) + D
        // Where S = setpoint, T = current value, β = strength, D = disturbance
        const error = params.setPoint - params.currentValue;
        const change = params.regulationStrength * error * dt + params.disturbance * dt;
        
        return params.currentValue + change;
    }
    
    public calculatePIDControl(params: HomeostaticParameters, dt: number, 
                              previousError: number, integral: number): 
                              { value: number, integral: number } {
        // PID: output = Kp·e + Ki·∫e + Kd·de/dt
        const Kp = params.regulationStrength;
        const Ki = 0.1;
        const Kd = 0.05;
        
        const error = params.setPoint - params.currentValue;
        integral += error * dt;
        const derivative = (error - previousError) / dt;
        
        const output = Kp * error + Ki * integral + Kd * derivative + params.disturbance;
        
        return {
            value: params.currentValue + output * dt,
            integral: integral
        };
    }
}

/**
 * MATHEMATICAL MODEL 5: Lotka-Volterra Predator-Prey Dynamics
 * dx/dt = αx - βxy  (prey)
 * dy/dt = δxy - γy  (predator)
 */
export interface PredatorPreyState {
    prey: number;
    predator: number;
    preyGrowthRate: number;
    predationRate: number;
    predatorGrowthRate: number;
    predatorDeathRate: number;
}

export class PredatorPreyModel {
    public calculateDynamics(state: PredatorPreyState, dt: number): PredatorPreyState {
        // Lotka-Volterra equations
        const dx = (state.preyGrowthRate * state.prey - 
                   state.predationRate * state.prey * state.predator) * dt;
        const dy = (state.predatorGrowthRate * state.prey * state.predator - 
                   state.predatorDeathRate * state.predator) * dt;
        
        return {
            prey: Math.max(0, state.prey + dx),
            predator: Math.max(0, state.predator + dy),
            preyGrowthRate: state.preyGrowthRate,
            predationRate: state.predationRate,
            predatorGrowthRate: state.predatorGrowthRate,
            predatorDeathRate: state.predatorDeathRate
        };
    }
}

/**
 * MATHEMATICAL MODEL 6: Phase-Coupled Oscillators (Kuramoto Model)
 * dθᵢ/dt = ωᵢ + (K/N) Σⱼ sin(θⱼ - θᵢ)
 * Synchronization of oscillatory systems
 */
export interface CoupledOscillatorState {
    phases: number[];    // Phase angles 0 to 2π
    frequencies: number[]; // Natural frequencies
    couplingStrength: number;
}

export class CoupledOscillatorModel {
    public calculateKuramotoDynamics(state: CoupledOscillatorState, dt: number): 
                                     CoupledOscillatorState {
        const N = state.phases.length;
        const K = state.couplingStrength / N;
        
        const newPhases: number[] = [];
        
        for (let i = 0; i < N; i++) {
            let couplingTerm = 0;
            
            // Sum over all other oscillators
            for (let j = 0; j < N; j++) {
                if (i !== j) {
                    couplingTerm += Math.sin(state.phases[j] - state.phases[i]);
                }
            }
            
            // dθᵢ/dt = ωᵢ + K·couplingTerm
            const dPhase = (state.frequencies[i] + K * couplingTerm) * dt;
            let newPhase = state.phases[i] + dPhase;
            
            // Wrap phase to [0, 2π]
            while (newPhase < 0) newPhase += 2 * Math.PI;
            while (newPhase >= 2 * Math.PI) newPhase -= 2 * Math.PI;
            
            newPhases.push(newPhase);
        }
        
        return {
            phases: newPhases,
            frequencies: state.frequencies,
            couplingStrength: state.couplingStrength
        };
    }
    
    public calculateOrderParameter(phases: number[]): { r: number, psi: number } {
        // Order parameter: re^(iψ) = (1/N) Σⱼ e^(iθⱼ)
        // r measures synchronization, ψ is average phase
        const N = phases.length;
        let realSum = 0;
        let imagSum = 0;
        
        for (const phase of phases) {
            realSum += Math.cos(phase);
            imagSum += Math.sin(phase);
        }
        
        const r = Math.sqrt(realSum * realSum + imagSum * imagSum) / N;
        const psi = Math.atan2(imagSum, realSum);
        
        return { r, psi };
    }
}

/**
 * MATHEMATICAL MODEL 7: Chemical Reaction Kinetics
 * d[A]/dt = -k[A][B]ⁿ (for reaction: A + nB → products)
 */
export interface ChemicalReactionParams {
    concentrationA: number;
    concentrationB: number;
    reactionRate: number;
    order: number; // Reaction order (0, 1, or 2)
}

export class ChemicalReactionModel {
    public calculateReactionRate(params: ChemicalReactionParams): number {
        // Rate = k[A]ᵃ[B]ᵇ
        switch (params.order) {
            case 0:
                return params.reactionRate; // Zero-order: rate is constant
            case 1:
                return params.reactionRate * params.concentrationA; // First-order
            case 2:
                return params.reactionRate * params.concentrationA * params.concentrationB; // Second-order
            default:
                return params.reactionRate * params.concentrationA * 
                       Math.pow(params.concentrationB, params.order);
        }
    }
}

/**
 * MATHEMATICAL MODEL 8: Attractor Dynamics
 * Stable states that systems evolve toward
 */
export interface AttractorState {
    x: number;
    y: number;
    attractorType: 'point' | 'limit_cycle' | 'strange';
    parameters: any;
}

export class AttractorModel {
    public calculateLorenzAttractor(state: AttractorState, dt: number): AttractorState {
        // Lorenz attractor: strange attractor in 3D
        // dx/dt = σ(y-x)
        // dy/dt = x(ρ-z) - y
        // dz/dt = xy - βz
        const sigma = 10;
        const rho = 28;
        const beta = 8/3;
        
        const x = state.x;
        const y = state.y;
        const z = state.parameters.z || 1;
        
        const dx = sigma * (y - x) * dt;
        const dy = (x * (rho - z) - y) * dt;
        const dz = (x * y - beta * z) * dt;
        
        return {
            x: x + dx,
            y: y + dy,
            attractorType: state.attractorType,
            parameters: { ...state.parameters, z: (z || 1) + dz }
        };
    }
}

/**
 * MATHEMATICAL MODEL 9: Information Theory & Entropy
 * H(X) = -Σ p(x) log₂ p(x)
 */
export class InformationTheoryModel {
    public calculateEntropy(probabilities: number[]): number {
        // Shannon entropy: H(X) = -Σ p(x) log₂ p(x)
        let entropy = 0;
        for (const p of probabilities) {
            if (p > 0) {
                entropy -= p * Math.log2(p);
            }
        }
        return entropy;
    }
    
    public calculateMutualInformation(jointProb: number[][], marginalX: number[], 
                                     marginalY: number[]): number {
        // I(X;Y) = ΣΣ p(x,y) log(p(x,y) / (p(x)p(y)))
        let mutualInfo = 0;
        
        for (let i = 0; i < marginalX.length; i++) {
            for (let j = 0; j < marginalY.length; j++) {
                const pXY = jointProb[i]?.[j] || 0;
                const pX = marginalX[i] || 0;
                const pY = marginalY[j] || 0;
                
                if (pXY > 0 && pX > 0 && pY > 0) {
                    mutualInfo += pXY * Math.log2(pXY / (pX * pY));
                }
            }
        }
        
        return mutualInfo;
    }
}

/**
 * MATHEMATICAL MODEL 10: Gene Regulatory Networks
 * dxᵢ/dt = fᵢ(x) - γᵢxᵢ
 * Hill function: f(x) = xⁿ / (Kⁿ + xⁿ)
 */
export class GeneRegulatoryModel {
    public calculateHillFunction(x: number, n: number, K: number): number {
        // Hill function: f(x) = xⁿ / (Kⁿ + xⁿ)
        // n is the Hill coefficient (cooperativity)
        const numerator = Math.pow(x, n);
        const denominator = Math.pow(K, n) + numerator;
        return numerator / denominator;
    }
    
    public calculateGeneExpression(input: number, params: {
        maxExpression: number;
        hillCoeff: number;
        halfMax: number;
        degradationRate: number;
    }): number {
        // Simple gene expression model
        const production = params.maxExpression * 
                          this.calculateHillFunction(input, params.hillCoeff, params.halfMax);
        const degradation = params.degradationRate;
        
        return production - degradation;
    }
}

// Life Behavior Math Manager
export class LifeBehaviorMathEngine {
    public growthModel: GrowthModel;
    public metabolicModel: MetabolicModel;
    public oscillatorModel: OscillatorModel;
    public homeostaticModel: HomeostaticModel;
    public predatorPreyModel: PredatorPreyModel;
    public coupledOscillatorModel: CoupledOscillatorModel;
    public chemicalReactionModel: ChemicalReactionModel;
    public attractorModel: AttractorModel;
    public informationTheoryModel: InformationTheoryModel;
    public geneRegulatoryModel: GeneRegulatoryModel;
    
    constructor() {
        this.growthModel = new GrowthModel();
        this.metabolicModel = new MetabolicModel();
        this.oscillatorModel = new OscillatorModel();
        this.homeostaticModel = new HomeostaticModel();
        this.predatorPreyModel = new PredatorPreyModel();
        this.coupledOscillatorModel = new CoupledOscillatorModel();
        this.chemicalReactionModel = new ChemicalReactionModel();
        this.attractorModel = new AttractorModel();
        this.informationTheoryModel = new InformationTheoryModel();
        this.geneRegulatoryModel = new GeneRegulatoryModel();
    }
    
    // Convenience methods that integrate multiple models
    public calculateLifeCycle(energy: number, growth: number, time: number): {
        energy: number;
        growth: number;
        vitality: number;
    } {
        // Integrated model combining energy and growth
        const metabolicParams: MetabolicParameters = {
            currentEnergy: energy,
            productionRate: 1.0,
            consumptionRate: 0.1,
            efficiencyFactor: 0.8,
            maxEnergy: 100
        };
        
        const growthParams: GrowthParameters = {
            initialSize: 1,
            growthRate: energy * 0.01,
            carryingCapacity: 100,
            currentSize: growth,
            time: time
        };
        
        const newEnergy = this.metabolicModel.calculateEnergyDynamics(metabolicParams, 0.1);
        const newGrowth = this.growthModel.calculateLogisticGrowth(growthParams, 0.1);
        
        // Vitality = combined measure of health
        const vitality = (newEnergy / 100) * (newGrowth / 100);
        
        return {
            energy: newEnergy,
            growth: newGrowth,
            vitality: vitality
        };
    }
}

